/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  AliasType,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './MutliSelectLOVField';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { isNotEmpty } from 'utils/isEmpty';

const MutliSelectLOVFieldManager: ComponentManagerFn<'MutliSelectLOVField'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MutliSelectLOVField'>(
      platform,
      'MutliSelectLOVField',
    ),
    type: 'MutliSelectLOVField',
    name: 'MultiSelect LOV Field',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'TriggerOnChange',
            'LOVGrid',
            'LOVMappings',
            'TriggerAfterServerValidate',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    defaultChildren: ['LOVGrid', 'LOVMappings'],
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => [
          'Email',
          'Phone',
          'Reference',
          'String',
          'UserID',
        ],
      },
      comboDatasourceAlias: {
        label: 'LOV Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['ReadOnlyDataStore'],
        required: true,
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to prevent blank query on LOV open',
      },
      returnValueAttribute: {
        label: 'Return Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      returnDisplayAttribute: {
        label: 'Return Display Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      popupWidth: {
        label: 'Popup Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Popup Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      delimiter: {
        label: 'Delimeter',
        type: 'string',
        helpText:
          'Set a sequence of one or more characters to seperate the selected values. Default: ~',
      },
      showGridFilters: {
        label: 'Show Grid Filters',
        type: 'boolean',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
      defaultFilterFocusColumn: {
        label: 'Default Filter Focus Column',
        type: 'ItemID',
        helpText:
          'Controls default filter focus. If a column Id is provided, the corresponding filter input will be focused when the popup opens.',
        initialFilter: 'LOVGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          const parentItem = platform.getItem(
            appUid,
            pageId,
            item?.parent || '',
          );

          if (parentItem?.type !== 'LOVGrid') {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      popupCssClassName: {
        label: 'Lov Popup CSS Class Name',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Look & Feel',
      },
    },

    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'returnValueAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'open':
          return false;
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ?? '',
    //   analyze: analyzeLOVField,
    devStyles: { display: 'inline-flex' },
    category: 'Forms',
    icon: { iconName: 'io-lov-field', iconType: 'cloudio' },
    excludeChildren: true,
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The Multi-Select LOV Field (List of Values) is a specialized form input that allows users to select multiple records from a popup grid and store them in a single field.
Unlike a standard Select (Dropdown), which is limited to simple lists, this component opens a modal window containing a full Data Grid. This allows users to search, filter, and view rich details (multiple columns) before selecting items. It is ideal for complex selection tasks like "Assign Multiple Users," "Add Related Products," or "Select Tags."

**1. Data Storage Strategy (The Delimiter)**<br/>
Since standard database fields typically hold single values, this component handles multiple selections by concatenating them into a single string.
* **View Attribute:** The attribute in your main Data Store where the selection is saved.
* **Delimiter:** The character used to separate the values. (Default: "~" tilde).
* **Example:** If the user selects IDs 101, 102, and 103, the value stored in the database is "101~102~103".

**2. Data Binding & Source**<br/>
You must define where the list of available options comes from and how they map to your form.
* **LOV Data Store Alias:** Select the external ReadOnly Data Store that populates the popup list.
* **Return Value Attribute:** The unique identifier from the selected record (e.g., "UserID") that will be stored in the View Attribute.
* **Return Display Attribute:** The human-readable text from the selected record (e.g., "UserName") that will be displayed in the input box.
* **Visual Output:** The user sees "John Doe, Jane Smith," while the database stores "U001~U002."

**3. Configuring the Popup**<br/>
The content of the popup is fully customizable because it is built using the standard grid architecture.
* **Child Component (LOV Grid):** This component must contain an **LOV Grid** child. This child acts exactly like a regular Data Grid—you add columns (String, Number, Date) to it to define what the user sees inside the popup.
* **Dimensions:** Customize the **Popup Width / Height** (e.g., 800 x 600) to accommodate the columns you are displaying.
* **Show Grid Filters:** Enables the standard filter row inside the popup, allowing users to search specifically by column (e.g., "Filter Department by 'Sales'").

**4. Advanced Logic**<br/>
* **Child Component (LOV Mappings):** Purpose: Map additional data from the selected records into other form fields. (Behavior: Since multiple items are selected, mapped values are typically also concatenated using the delimiter).
* **Do Not Prefetch:**
    * **Enabled:** Defers data loading until the user actually opens the popup. Essential for performance if the source list contains thousands of records.`,
  };
};

export default MutliSelectLOVFieldManager;
